import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  SAPSection,
  OomPahPlayer,
  SAPChordRow,
  SAPCallout,
  SAPQuote,
  SAPPracticeLadder,
} from "@/components/sap/SAPShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { NoteImage, NoteImagePair } from "@/components/mt/NoteImage";

export default function SingAndPlay6() {
  const { lesson, course, accent } = findLesson("sing-and-play", "6");
  return (
    <LessonShell
      course={course}
      courseSlug="sing-and-play"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >

      <AnimatedExplainer
        src="/animations/sing-and-play/SP6-Gospel-Waymaker-Animated.mp4"
        caption={`Modern gospel — open fifths + ghost-note thumb · I-V-vi-IV`}
        hint="≈ 22 sec · arena anchor"
      />
      {/* ── 1. Big idea ─────────────────────────────────────────────────── */}
      <SAPSection eyebrow="Lesson 6 · Gospel Modern" title="Way Maker — The Doxology Loop">
        <p>
          Written by Nigerian worship leader Sinach in 2015, "Way Maker" has been covered by
          more than 60 Christian artists and translated into around 50 languages, and is sung
          in stadiums and living rooms alike. The secret is its simplicity: four chords,
          cycling endlessly, creating a wave of escalating emotion each time round.
        </p>
        <p>
          Those four chords form what this lesson calls the <strong>doxology loop</strong> —
          IV–I–V–vi in C major (F–C–G–Am). You will recognise this progression instantly
          once you hear it: it's in "Let Her Go" by Passenger, "With or Without You" by
          U2, countless CCM anthems. Learn it once; use it for life.
        </p>
        <SAPCallout label="Why this progression works" variant="insight">
          The loop starts on the IV chord — that slight tension of "not quite home" — then
          resolves to I, reaches up to V, and finally rests on vi before starting over.
          It creates a gentle, perpetual yearning that makes it perfect for worship.
        </SAPCallout>
      </SAPSection>

      {/* ── 2. Reference video ──────────────────────────────────────────── */}
      <YouTubeEmbed
        videoId="QM8jQHE5AAk"
        title="Reference · Sinach — Way Maker (Studio Version)"
        caption="Sinach — Way Maker · Listen to the piano texture: light, open, guitar-imitation. That's what we're building."
      />

      {/* ── 3. The progression ──────────────────────────────────────────── */}
      <SAPSection eyebrow="The Harmony" title="IV – I – V – vi: Click every chord">
        <p>
          In C major, the doxology loop runs: <strong>F → C → G → Am</strong>. The
          Roman numerals are IV–I–V–vi. Every chord is diatonic — no accidentals, no
          surprises — yet the loop never feels static because the voice leading inside it
          keeps moving.
        </p>

        <RomanNumeralAnalysis
          label="Verse & Chorus — IV I V vi"
          caption="F is the pre-dominant that tips the whole loop forward. C is the gravitational centre. G is the tension that must resolve. Am is the wistful turn before it all begins again."
          chords={[
            { chord: "F", rn: "IV", function: "Pre-dominant", pitches: ["F3","A3","C4","F4"] },
            { chord: "C", rn: "I",  function: "Tonic",        pitches: ["C3","E3","G3","C4"] },
            { chord: "G", rn: "V",  function: "Dominant",     pitches: ["G3","B3","D4","G4"] },
            { chord: "Am", rn: "vi", function: "Submediant",     pitches: ["A3","C4","E4","A4"] },
          ]}
        />

        <SAPChordRow
          label="Click the chords — hear the doxology loop"
          chords={[
            { label: "F",  rn: "IV", pitches: ["F3","A3","C4","F4"], fn: "subdominant" },
            { label: "C",  rn: "I",  pitches: ["C3","E3","G3","C4"], fn: "tonic"       },
            { label: "G",  rn: "V",  pitches: ["G3","B3","D4","G4"], fn: "dominant"    },
            { label: "Am", rn: "vi", pitches: ["A3","C4","E4","A4"], fn: "submediant"     },
          ]}
          caption="Notice how Am shares two notes with C (E and G). That shared DNA is why moving between them feels smooth."
        />
      </SAPSection>

      {/* ── 4. The fifth-chord RH strategy ──────────────────────────────── */}
      <SAPSection eyebrow="Technique · Right Hand" title="The Fifth-Chord Strategy">
        <p>
          The most common mistake at this stage is trying to play three-note block chords
          in the right hand. That gives a thick, heavy sound — like a church organ, not a
          guitar. Jason's solution: the <strong>fifth-chord strategy</strong>.
        </p>
        <p>
          Instead of playing the full triad (root + third + fifth), play just the{" "}
          <strong>root and the fifth</strong>. On C major: C and G. On F major: F and C.
          These open voicings ring clear, sustain beautifully, and leave space for the
          melody — exactly what worship piano needs.
        </p>

        <SAPCallout label="The fifth-chord shapes" variant="tip">
          <strong>F fifth:</strong> F–C &nbsp;·&nbsp;
          <strong>C fifth:</strong> C–G &nbsp;·&nbsp;
          <strong>G fifth:</strong> G–D &nbsp;·&nbsp;
          <strong>Am fifth:</strong> A–E
          <br />
          Play each one around middle C, or an octave higher.
          <br />
          These shapes sit naturally under the fingers. Practice each one until the hand
          snaps into position without thinking.
        </SAPCallout>

        <SAPQuote>
          It's already sounding sort of like what a guitar player would play.
        </SAPQuote>
      </SAPSection>

      {/* ── 5. Ghost-note thumb & LH octave bass ─────────────────────────── */}
      <SAPSection eyebrow="Technique · Left Hand" title="Octave Bass + Ghost-Note Thumb">
        <p>
          The left hand has two jobs: anchor the harmony and imitate the rhythm guitar.
          We do both with one pattern: an <strong>octave root</strong> on the beat, and a{" "}
          <strong>ghost-note fifth</strong> between the beats.
        </p>
        <p>
          Play the root in octaves (e.g. two Fs an octave apart, low in the bass, for the F chord). Then, softly — barely
          audible — add the fifth of the chord (C) between the beats with your thumb. That
          soft thumb note is the ghost. It should feel like the bass string of a guitar
          ringing on after being plucked.
        </p>



        <SAPCallout label="Voice-leading tip: Am → F" variant="insight">
          When you move from Am to F, the C note appears in both chords (Am = A–C–E,
          F = F–A–C). Keep that C in place in your right hand — let it be a <em>pivot
          note</em>. Only move the other fingers. This is common-tone voice leading, and
          it makes the transition silky smooth.
        </SAPCallout>
      </SAPSection>

      {/* ── 6. OomPah interactive player ────────────────────────────────── */}
      <SAPSection eyebrow="Interactive · Play Along" title="The Oom-Pah Loop in C">
        <p>
          Press play to hear the doxology loop at 90 bpm with the Oom-Pah pattern.
          Watch the grid: LH bass hits on every beat, RH chord hits on off-beats. That
          interlock is the engine.
        </p>
        <OomPahPlayer
          progression={[
            { label: "F",  bars: 2, lhRoot: "F2",  lhFifth: "C3",  rh: ["F4","C5"] },
            { label: "C",  bars: 2, lhRoot: "C3",  lhFifth: "G3",  rh: ["C4","G4"] },
            { label: "G",  bars: 2, lhRoot: "G2",  lhFifth: "D3",  rh: ["G4","D5"] },
            { label: "Am", bars: 2, lhRoot: "A2",  lhFifth: "E3",  rh: ["A4","E5"] },
          ]}
          bpm={90}
          label="WAY MAKER — C major doxology loop"
        />
        <SAPCallout label="Slow it down" variant="tip">
          Drag the BPM slider to 70 and play along physically. Your goal is to feel where
          the LH bass lands and where the RH chord breathes — like two people taking turns
          in a conversation.
        </SAPCallout>
      </SAPSection>

      {/* ── 7. Practice ladder ──────────────────────────────────────────── */}
      <SAPSection eyebrow="Your Practice Plan" title="30-Minute Way Maker Workout">
        <SAPPracticeLadder
          intro="Work through these rungs in order. Don't rush to the top — each rung builds the next one."
          rungs={[
            {
              title: "LH octave roots alone",
              minutes: 5,
              detail: "F–C–G–Am as octaves only. No rhythm yet. Just lock the hand positions into muscle memory.",
              accent: "amber",
            },
            {
              title: "Add ghost-note thumb",
              minutes: 5,
              detail: "Keep the octave bass, add the soft fifth between beats. Ghost = barely audible. If you can hear it clearly, it's too loud.",
              accent: "purple",
            },
            {
              title: "RH fifth chords, block form",
              minutes: 5,
              detail: "Just hold each fifth chord for 2 bars while LH plays. F–C, then C–G, then G–D, then A–E.",
              accent: "cyan",
            },
            {
              title: "RH off-beat hits",
              minutes: 5,
              detail: "Now move the RH to off-beats only. Beat 1 = LH root; &-of-1 = RH fifth chord. Build the interlock.",
              accent: "emerald",
            },
            {
              title: "Hands together, full loop",
              minutes: 5,
              detail: "Play the complete IV–I–V–vi at 80 bpm. Focus on the ghost thumb. Let the pattern breathe.",
              accent: "rose",
            },
            {
              title: "Sing + play",
              minutes: 5,
              detail: "Add your voice. Start on \"You are here\" — the melody sits beautifully over this progression. Don't stop playing if you miss a note.",
              accent: "purple",
            },
          ]}
        />
        <SAPQuote>
          Find me other gospel songs as well which you can, you know, benefit from using
          this fifth chord concept, or maybe even non-gospel like folk songs, country
          songs and the like.
        </SAPQuote>
      </SAPSection>


      {/* ── Jason's notes ────────────────────────────────────────────── */}
      <SAPSection eyebrow="Jason's chord chart" title="Handwritten teaching notes">
        <p>
          This is Jason's original handwritten chord chart for this song — the same one he
          uses in the video lesson. Click to zoom in and follow along.
        </p>
        <NoteImage
          src="/notes/sing-and-play/06-waymaker.png"
          caption="Jason's chord chart — Waymaker in A major"
        />
      </SAPSection>
    </LessonShell>
  );
}
